<template>
  <el-dialog v-if="props.componentType === 'dialog'" v-model="visible" class="small-s no-header no-padding auto-height" @close="visible = false" :append-to-body="true">
    <div class="el-message-box el-message-box--center" tabindex="-1">
      <div class="el-message-box__header show-close">
        <div class="el-message-box__title"><span>{{ $t('Redeem License') }}</span></div>
      </div>
      <div id="el-id-3013-18" class="el-message-box__content">
        <div class="el-message-box__container">
          <div class="el-message-box__message">
            <p>{{ $t('The license will be bound to your account, and extend the expiration date.') }}</p>
          </div>
        </div>
      </div>
      <div class="flex-row redeem-form">
        <el-input v-model.trim="redeemCode" @input="handleRedeemCodeChange" name="redeem-code" :maxlength="redeemCodeLen" v-loading="redeemCodeLoading" :placeholder="$t('Enter your redemption code')"></el-input>
      </div>
      <div class="el-message-box__btns">
        <el-button @click="visible = false">{{ $t('Cancel') }}</el-button>
        <el-button type="primary" @click="handleRedeemCode(false)" :loading="redeemBtnLoading">
          {{ $t('Redeem-2') }}
        </el-button>
      </div>
      <p class="result-msg" v-if="redeemResultMsg != ''" :class="{ 'is-error': redeemResultIsError }">{{ redeemResultMsg }}</p>

    </div>
  </el-dialog>
  <div v-else class="form-section flex-row flex-align-center flex-justify-between" ref="refFormSection">
    <div class="flex-column flex-1 flex-justify-between">
      <div class="form-title">
        <h5>{{ $t('Redeem-2') }}</h5>
        <span class="form-desc">{{ $t('The license will be bound to your account, and extend the expiration date.') }}</span>
      </div>
      <div class="flex-row redeem-form">
        <div class="flex-1 redeem-input">
          <el-input v-model.trim="redeemCode" @input="handleRedeemCodeChange" name="redeem-code" :maxlength="redeemCodeLen" v-loading="redeemCodeLoading" :placeholder="$t('Enter your redemption code')"></el-input>
        </div>
        <el-button class="redeem-btn" type="primary" @click="handleRedeemCode(false)" :loading="redeemBtnLoading">
          {{ $t('Redeem-2') }}
        </el-button>
        <p class="result-msg" v-if="redeemResultMsg != ''" :class="{ 'is-error': redeemResultIsError }">{{ redeemResultMsg }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { getOffsetCoupon, useOffsetCoupon } from '@/api/user/user'
import emitter from '@/libs/emitter'
import gtagTools from '@/libs/gtag-tools'
import { appStore, userStore } from '@/stores'
import { ElMessage, ElMessageBox } from 'element-plus'
import { computed, getCurrentInstance, ref } from 'vue'

const { $t } = getCurrentInstance().proxy

const props = defineProps({
  // 组件类型： dialog: 弹窗，section: 表单
  componentType: {
    type: String,
    default: 'dialog'
  }
})

// 弹窗类型时显示状态
const visible = defineModel(false)

// 是否含有终身plan
const hasLifetimePlan = computed(() => userStore.planList.filter(item => item.planType === 'lifetime').length > 0)
const redeemCode = ref('')
const redeemCodePlanData = {}
const redeemCodeLoading = ref(false)
const redeemBtnLoading = ref(false)
const redeemBtnDisabled = ref(true)
const redeemResultIsError = ref(true)
const redeemResultMsg = ref('')
const redeemCodeLen = 16


// 检测是否可以正常兑换
const checkCanRedeem = (isAI, isSign) => {
  // 含有终身情况下提示切换账号
  if (!isAI && hasLifetimePlan.value && !isSign) {
    ElMessageBox.alert($t('The current account already has a perpetual plan. If you need to redeem it again, please change to another account.'), '', {
      confirmButtonText: $t('Ok'),
    })
    return false
  }
  return true
}
const handleRedeemCodeChange = async code => {
  // 检测兑换码
  redeemResultMsg.value = ''
  redeemBtnDisabled.value = true
  if (code.length === redeemCodeLen) {
    redeemCodeLoading.value = true
    try {
      const { data } = await getOffsetCoupon(code.trim())
      Object.assign(redeemCodePlanData, data)
      if (!checkCanRedeem(data.isAI, data.isSign)) {
        redeemCodeLoading.value = false
        redeemBtnDisabled.value = false
        return false
      }
      const getRedeemTime = redeemData => {
         const  redeemTypeMap = new Map([
          ['lifetime', $t('Perpetual')],
          ['quarterly', ` ${redeemData.months} ${$t('Months')}`],
          ['annually', ` ${redeemData.months} ${$t('Months')}`],
          ['monthly', ` ${redeemData.months} ${$t(redeemData.months > 0 ? 'Month' : 'Months')}`],
        ])
        return  redeemTypeMap.get(redeemData.subscriptionType)
      }
      
      const getRedeemProduct = (redeemData) => {
        if (redeemData.isAI) return $t('UPDF AI')
        else if (redeemData.isSign) return $t('Sign')
        else return $t('UPDF')
      }
      for (const index in redeemCodePlanData) {
        redeemCodePlanData[index].isProduct = getRedeemProduct(redeemCodePlanData[index])
      }
      const redeemProductList = []
      Object.values(redeemCodePlanData).forEach(item => {
        if (item.isProduct) {
          redeemProductList.push(`${getRedeemProduct(item)} ${getRedeemTime(item)}`)
        }
      })
      redeemResultMsg.value = `${$t('Redeemable Membership Duration:')} ${redeemProductList.join(appStore.isCN ? '，' : ', ')}`
      redeemResultIsError.value = false
      redeemCodeLoading.value = false
      redeemBtnDisabled.value = false
      gtagTools.redeemVerify('Success')
    } catch (err) {
      console.log(err)
      redeemCodeLoading.value = false
      setRedeemCodeError(err.code)
      gtagTools.redeemVerify(redeemResultMsg.value)
    }
  }
}
const handleRedeemCode = async (retry = false) => {
  // 兑换兑换码
  const code = redeemCode.value
  if (code && code.length !== redeemCodeLen) {
    setRedeemCodeError(0)
    return
  }
  if (!checkCanRedeem(redeemCodePlanData.isAI, redeemCodePlanData.isSign)) {
    return false
  }
  if (redeemBtnLoading.value && !retry) return
  redeemBtnLoading.value = true
  try {
    await useOffsetCoupon(code, retry)
    redeemBtnLoading.value = false
    redeemResultMsg.value = ''
    redeemCode.value = ''
    ElMessage.success($t('Redeem success.'))
    // 通知刷新plan列表
    setTimeout(() => {
      emitter.emit('refreshPlanList')
    }, 1000)
    visible.value = false
    gtagTools.redeem(redeemCodePlanData.subscriptionType, redeemCodePlanData.channelMerchantsName, 'Success')
  } catch (err) {
    if (!retry) {
      handleRedeemCode(true)
    } else {
      console.log(err)
      redeemBtnLoading.value = false
      setRedeemCodeError(err.code)
      gtagTools.redeem('', '', redeemResultMsg.value)
    }
  }
}
const setRedeemCodeError = resCode => {
  // 设置兑换码输入框下错误或检测成功信息
  redeemResultIsError.value = true
  if ([4005, 4009, 4014, 4028, 4023].includes(resCode)) {
    redeemResultMsg.value = $t(`http_error_${resCode}`)
  } else {
    redeemResultMsg.value = $t(`Incorrect license code.`)
  }
}
</script>

<style lang="scss" scoped>
.result-msg {
  width: 100%;
  color: #3db5f9;
  font-size: 14px;
  margin-top: 8px;
  &.is-error {
    color: var(--el-color-error);
  }
}

.el-message-box {
  .redeem-form {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    margin-top: 20px;
    position: relative;
    .redeem-btn {
      min-width: 120px;
      margin-left: 8px;
    }
    
  }
}

.form-section {
  .flex-row {
    flex-wrap: wrap;
  }
  .redeem-form {
    margin-top: 16px;
    gap: 8px;
    .redeem-btn {
      min-width: 108px;
    }
  }
}

@media screen and (max-width: 500px) {
  .form-section {
    flex-direction: column;
    align-items: start;
    gap: 12px;
    .redeem-form {
      flex-direction: column;
      align-items: start;
      .redeem-input {
        width: 100%;
      }
    }
  }
}
</style>
<style lang="scss">
.ar-SA {
  .redeem-form {
    .redeem-btn {
      margin-left: 0;
      margin-right: 8px;
    }
    .result-msg {
      text-align: right;
    }
  }
}
</style>
